import type { SponsorDisplayMode } from '@/lib/contract'
import type { InstallAvailability, PlatformAdapter, ShareResult } from './types'

/* Spec PWA-001 — implementacao web (PWA) do contrato de plataforma. */

type BeforeInstallPromptEvent = Event & {
    prompt(): Promise<void>
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function isIosUserAgent(userAgent: string, maxTouchPoints: number): boolean {
    if (/iPad|iPhone|iPod/.test(userAgent)) return true
    // iPadOS 13+ se apresenta como Mac com tela sensivel ao toque
    return /Macintosh/.test(userAgent) && maxTouchPoints > 1
}

export function resolveDisplayMode(matchesStandalone: boolean, navigatorStandalone: boolean | undefined): SponsorDisplayMode {
    return matchesStandalone || navigatorStandalone === true ? 'standalone' : 'browser'
}

export function createWebPlatform(win: Window = window): PlatformAdapter {
    let deferredPrompt: BeforeInstallPromptEvent | null = null
    let installed = false
    const listeners = new Set<() => void>()
    const notify = () => listeners.forEach(l => l())

    // Capturado cedo (import do modulo) para nao perder o evento disparado antes da primeira tela.
    win.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault()
        deferredPrompt = event as BeforeInstallPromptEvent
        notify()
    })
    win.addEventListener('appinstalled', () => {
        installed = true
        deferredPrompt = null
        notify()
    })

    const displayMode = (): SponsorDisplayMode => resolveDisplayMode(
        win.matchMedia?.('(display-mode: standalone)').matches ?? false,
        (win.navigator as Navigator & { standalone?: boolean }).standalone,
    )

    return {
        client: 'web',
        displayMode,
        deviceLanguages: () => {
            const nav = win.navigator
            return nav.languages?.length ? nav.languages : [nav.language]
        },
        storage: {
            get: (key) => { try { return win.localStorage.getItem(key) } catch { return null } },
            set: (key, value) => { try { win.localStorage.setItem(key, value) } catch { /* storage bloqueado */ } },
            remove: (key) => { try { win.localStorage.removeItem(key) } catch { /* storage bloqueado */ } },
        },
        install: {
            availability: (): InstallAvailability => {
                if (installed || displayMode() === 'standalone') return { kind: 'installed' }
                if (deferredPrompt) return { kind: 'prompt' }
                if (isIosUserAgent(win.navigator.userAgent, win.navigator.maxTouchPoints ?? 0)) return { kind: 'ios-manual' }
                return { kind: 'unsupported' }
            },
            prompt: async () => {
                if (!deferredPrompt) return false
                const event = deferredPrompt
                deferredPrompt = null
                await event.prompt()
                const { outcome } = await event.userChoice
                notify()
                return outcome === 'accepted'
            },
            subscribe: (listener) => {
                listeners.add(listener)
                return () => { listeners.delete(listener) }
            },
        },
        openExternal: (url) => {
            win.open(url, '_blank', 'noopener,noreferrer')
        },
        share: async ({ title, url }): Promise<ShareResult> => {
            const nav = win.navigator
            if (typeof nav.share === 'function') {
                try {
                    await nav.share({ title, url })
                    return 'shared'
                } catch (error) {
                    return (error as DOMException)?.name === 'AbortError' ? 'cancelled' : 'unavailable'
                }
            }
            try {
                await nav.clipboard.writeText(url)
                return 'copied'
            } catch {
                return 'unavailable'
            }
        },
        copyText: async (text) => {
            try {
                await win.navigator.clipboard.writeText(text)
                return true
            } catch {
                return false
            }
        },
    }
}
